import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lightning, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import { ChipSelector, CopyButton, downloadAsText } from "@/components/shared";
import { API } from "@/lib/api";

// ============= HOOK + CAPTION SECTION =============
const HookCaption = () => {
  const [hooks, setHooks] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [form, setForm] = useState({
    script: "",
    platform: "instagram",
    hook_number: null,
    caption_style: "short-punchy",
  });
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);

  const captionStyles = [
    { value: "short-punchy", label: "Short & Punchy" },
    { value: "storytelling", label: "Storytelling" },
    { value: "bold", label: "Bold" },
    { value: "warm", label: "Warm" },
    { value: "thought-leader", label: "Thought Leader" },
  ];

  useEffect(() => {
    const fetchHooks = async () => {
      try {
        const [hooksRes, catsRes] = await Promise.all([
          axios.get(`${API}/hooks`),
          axios.get(`${API}/hooks/categories`),
        ]);
        setHooks(hooksRes.data.hooks);
        setCategories(catsRes.data.categories);
      } catch (e) {
        console.error("Error fetching hooks:", e);
      }
    };
    fetchHooks();
  }, []);

  const filteredHooks = hooks.filter((h) => {
    const matchesCategory = selectedCategory === "all" || h.category === selectedCategory;
    const matchesSearch = h.hook.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const generateHookCaption = async () => {
    if (!form.script) return;
    setLoading(true);
    setOutput(null);
    try {
      const res = await axios.post(`${API}/hooks/generate`, form);
      setOutput(res.data);
    } catch (e) {
      console.error("Error generating hook/caption:", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900">Hook + Caption Generator</h2>
        <p className="text-zinc-500 mt-1">Generate viral hooks and captions from your script (200 hooks available)</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Script Input */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-4 sm:p-6">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-2">
              Paste Your Script
            </label>
            <textarea
              value={form.script}
              onChange={(e) => setForm({ ...form, script: e.target.value })}
              className="textarea-field h-48"
              placeholder="Paste your finished script here..."
              data-testid="hook-script-input"
            />
          </div>

          <div className="card p-4 sm:p-6 space-y-4">
            <ChipSelector
              label="Platform"
              options={["tiktok", "instagram", "linkedin"]}
              selected={form.platform}
              onChange={(v) => setForm({ ...form, platform: v })}
            />
            <ChipSelector label="Caption Style" options={captionStyles} selected={form.caption_style} onChange={(v) => setForm({ ...form, caption_style: v })} />
            
            <div className="flex items-center gap-4 pt-2">
              <label className="flex items-center gap-2 text-sm text-zinc-600">
                <input
                  type="checkbox"
                  checked={form.hook_number === null}
                  onChange={() => setForm({ ...form, hook_number: null })}
                  className="rounded border-zinc-300 text-[#E91E8C] focus:ring-[#E91E8C]"
                />
                Auto-pick best hook
              </label>
            </div>

            <button
              onClick={generateHookCaption}
              disabled={!form.script || loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="generate-hook-btn"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generating...
                </>
              ) : (
                <>
                  <Lightning size={18} />
                  Generate Hook + Caption
                </>
              )}
            </button>
          </div>

          {/* Output */}
          {output && (
            <div className="card p-4 sm:p-6">
              <h4 className="font-semibold text-zinc-900 mb-4">Generated Content</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-zinc-500">
                  <span className="px-2 py-0.5 bg-[#E91E8C]/10 text-[#E91E8C] rounded-full font-medium">
                    Hook #{output.selected_hook_number}
                  </span>
                  <span className="capitalize">{output.selected_hook_category}</span>
                </div>
                <div className="bg-zinc-50 rounded-lg p-4 whitespace-pre-wrap text-zinc-700 text-sm">
                  {output.result}
                </div>
                <div className="flex items-center gap-2">
                  <CopyButton text={output.result} label="Copy Everything" />
                  <button
                    onClick={() => downloadAsText(output.result, `hook-${output.selected_hook_category || "content"}.txt`)}
                    className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#E91E8C] px-2.5 py-1.5 rounded-lg hover:bg-zinc-50 transition-all"
                    data-testid="download-hook-btn"
                  >
                    <DownloadSimple size={14} />
                    Download
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Hook Browser */}
        <div className="card p-4 max-h-[80vh] overflow-hidden flex flex-col">
          <h4 className="font-semibold text-zinc-900 mb-3">Hook Library ({hooks.length})</h4>
          
          <div className="relative mb-3">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search hooks..."
              className="input-field pl-9 text-sm"
              data-testid="hook-search"
            />
          </div>

          <div className="flex flex-wrap gap-1 mb-3">
            <button
              onClick={() => setSelectedCategory("all")}
              className={`text-xs px-2 py-1 rounded-full ${selectedCategory === "all" ? "bg-[#E91E8C] text-white" : "bg-zinc-100 text-zinc-600"}`}
            >
              All
            </button>
            {Object.keys(categories).map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`text-xs px-2 py-1 rounded-full capitalize ${selectedCategory === cat ? "bg-[#E91E8C] text-white" : "bg-zinc-100 text-zinc-600"}`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto space-y-2">
            {filteredHooks.slice(0, 50).map((hook) => (
              <button
                key={hook.number}
                onClick={() => setForm({ ...form, hook_number: hook.number })}
                className={`w-full text-left p-2 rounded-lg text-xs transition-all ${
                  form.hook_number === hook.number
                    ? "bg-[#E91E8C]/10 border border-[#E91E8C]/30"
                    : "bg-zinc-50 hover:bg-zinc-100"
                }`}
              >
                <span className="font-medium text-zinc-500">#{hook.number}</span> {hook.hook}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HookCaption;
